import React from 'react';

// Click-a-header sorting for every ILMSTable, without each table having to
// say how its columns sort: the cell's own displayed text is the sort key.
// Numbers (₹ amounts, litres, %), dates ("14 Sep 2026") and codes
// ("COW-000014") each compare the way a person reads them.

export type SortDir = 'asc' | 'desc';
export interface TableSort { key: string; dir: SortDir }

/** The visible text of a rendered cell. */
export function nodeText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(' ');
  if (React.isValidElement(node)) {
    const props = node.props as Record<string, unknown>;
    const inner = nodeText(props.children as React.ReactNode);
    if (inner.trim()) return inner;
    // Badges and pills that take their text as a prop (status="Active").
    for (const k of ['status', 'label', 'value', 'title']) {
      if (typeof props[k] === 'string' || typeof props[k] === 'number') return String(props[k]);
    }
  }
  return '';
}

const EMPTY = new Set(['', '—', '-', '–']);
const NUMERIC = /^[₹$+\-−]?\s*[\d,]*\.?\d+\s*(%|l|kg|yrs?|days?|mo)?$/i;

function asNumber(text: string): number | null {
  const t = text.trim();
  if (!NUMERIC.test(t)) return null;
  const n = Number(t.replace(/[₹$,\s%a-z]/gi, '').replace('−', '-'));
  return Number.isFinite(n) ? n : null;
}

function asDate(text: string): number | null {
  if (!/\d{4}/.test(text) || !/[a-z]{3}|\d{1,2}[-/]\d{1,2}/i.test(text)) return null;
  const t = Date.parse(text);
  return Number.isNaN(t) ? null : t;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Blanks always sort last, whichever way. */
export function compareText(a: string, b: string, dir: SortDir): number {
  const ae = EMPTY.has(a.trim());
  const be = EMPTY.has(b.trim());
  if (ae || be) return ae === be ? 0 : ae ? 1 : -1;
  const sign = dir === 'asc' ? 1 : -1;
  const an = asNumber(a);
  const bn = asNumber(b);
  if (an != null && bn != null) return (an - bn) * sign;
  const ad = asDate(a);
  const bd = asDate(b);
  if (ad != null && bd != null) return (ad - bd) * sign;
  return collator.compare(a, b) * sign;
}

export function sortRows<T>(
  rows: T[],
  sort: TableSort | null | undefined,
  columns: { key: string; render: (row: T, i: number) => React.ReactNode; sortValue?: (row: T) => string | number | null | undefined }[],
): T[] {
  if (!sort) return rows;
  const col = columns.find((c) => c.key === sort.key);
  if (!col) return rows;
  const keyed = rows.map((row, i) => {
    const v = col.sortValue ? col.sortValue(row) : null;
    return { row, text: v != null ? String(v) : nodeText(col.render(row, i)) };
  });
  keyed.sort((x, y) => compareText(x.text, y.text, sort.dir));
  return keyed.map((k) => k.row);
}

/** Header click: ascending, then descending, then back to the list's own order. */
export function nextSort(current: TableSort | null | undefined, key: string): TableSort | null {
  if (!current || current.key !== key) return { key, dir: 'asc' };
  if (current.dir === 'asc') return { key, dir: 'desc' };
  return null;
}
